import { Directive, TemplateRef, ViewContainerRef, computed, effect, inject, input, untracked } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/state/app.state';

@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  templateRef = inject(TemplateRef);
  viewContainer = inject(ViewContainerRef);
  store = inject<Store<AppState>>(Store<AppState>);

  readonly hasPermission = input.required<string>();
  readonly hasPermissionGroup = input<number>(undefined);
  readonly hasPermissionToAll = input<number[]>(undefined);
  readonly hasPermissionToAny = input<number[]>(undefined);
  readonly hasPermissionElse = input<TemplateRef<unknown>>(undefined);
  readonly companyContext = this.store.selectSignal((s) => s.companyContext);
  readonly userInfo = this.store.selectSignal((s) => s.userInfo);

  readonly permissions = computed(() =>
    (this.hasPermission() ?? '')
      .split(';')
      .map((p) => p.trim())
      .filter(Boolean),
  );

  readonly renderElement = computed(() => {
    const userInfo = this.userInfo();
    const companyContext = this.companyContext();

    if (!companyContext || companyContext.id == null || !userInfo) {
      return false;
    }

    const perms = this.permissions();

    if (perms.length === 0) return true;

    return perms.some((permission) => {
      const canForGroup = (groupId: number | undefined) =>
        userInfo.userPermission?.canWithContext?.(permission, companyContext, groupId) === true;

      const allGroups = this.hasPermissionToAll();
      const anyGroups = this.hasPermissionToAny();

      if (allGroups?.length) {
        return allGroups.every((groupId) => canForGroup(groupId));
      }
      if (anyGroups?.length) {
        return anyGroups.some((groupId) => canForGroup(groupId));
      }
      return canForGroup(this.hasPermissionGroup());
    });
  });

  private readonly renderEff = effect(() => {
    const _ = this.renderElement();
    untracked(() => this.render());
  });

  private lastRendered: 'then' | 'else' | 'none' = 'none';
  render(): void {
    const showThen = this.renderElement();
    const hasPermissionElse = this.hasPermissionElse();
    const target = showThen ? 'then' : hasPermissionElse ? 'else' : 'none';
    if (this.lastRendered === target) {
      return;
    }
    this.lastRendered = target;

    if (this.viewContainer?.length) {
      this.viewContainer.clear();
    }

    if (showThen) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else if (hasPermissionElse != null) {
      this.viewContainer.createEmbeddedView(hasPermissionElse);
    }
  }
}
